import { retryTransient } from '@agos/api-client'
import { useGetCampaignsWheel, usePostCampaignsWheelSpin } from '@agos/api-client/marketing'
import { useCustomerAuth, useSecond } from '@agos/customer-core'
import { T, tt, useModalFocus } from '@agos/shared'
import { useEffect, useRef, useState } from 'react'

import { SPIN_DURATION_MS, rotationAt, segmentArc, spinDestination } from './wheel'
import {
  landedOn,
  prizeNoteOf,
  countdownTo,
  spinBrokenNote,
  spinRefusal,
  wheelCampaignOf,
  wheelIsOffered,
  wheelNote,
  type WheelCampaign,
} from './wheel-campaign'

/**
 * The prize wheel, mounted app-wide as legacy mounts it.
 *
 * **The server draws the prize.** This animates towards a segment it has already been told, which is
 * why `wheel.ts` exists and is tested: getting the arithmetic wrong stops the wheel on one slice while
 * the screen announces another, and that reads as the game being rigged rather than as a bug.
 *
 * Measured on this data the campaign is **switched off** — `wheel_campaign_settings.is_active` is
 * false, against 2 640 spins in the history — so nothing here draws today. It is built rather than
 * filed because a franchise turning it back on is a settings toggle, not a release.
 */
/**
 * `hh:mm:ss` until the next spin, ticking.
 *
 * Its own component, as legacy has it, so the one-second tick exists only while a countdown is on
 * screen — not for every customer who can see the wheel's button.
 */
function Countdown({ to }: { to: string }) {
  const remaining = countdownTo(to, useSecond())
  if (remaining === null) return null
  return (
    <span className="font-mono font-bold text-primary" data-wheel-countdown>
      {remaining}
    </span>
  )
}

export function SpinTheWheel() {
  const user = useCustomerAuth((state) => state.user)
  const { data } = useGetCampaignsWheel({
    // No session, no wheel - and asking without one answers 401 on every route the customer visits.
    /* FB-835, §9.549 - **`enabled` is what prevents that 401, not `retry: false`.** The retry setting only decided what
       a 500 or a dropped connection did, and it took the customer's one spin of the day away silently. */
    query: { enabled: user !== null, retry: retryTransient, staleTime: 60_000 },
  })
  const spin = usePostCampaignsWheelSpin()

  const campaign = wheelCampaignOf(data)
  const [open, setOpen] = useState(false)

  if (!wheelIsOffered(campaign, user !== null) || campaign === null) return null

  return (
    <>
      <button
        type="button"
        data-wheel-open
        onClick={() => setOpen(true)}
        aria-label={tt('Koleso šťastia')}
        className="fixed bottom-24 md:bottom-6 right-4 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-xl flex items-center justify-center text-2xl active:scale-95 transition-transform"
      >
        🎡
      </button>
      {open && (
        <WheelDialog
          campaign={campaign}
          busy={spin.isPending}
          onSpin={() => spin.mutateAsync()}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  )
}

function WheelDialog({
  campaign,
  busy,
  onSpin,
  onClose,
}: {
  campaign: WheelCampaign
  busy: boolean
  onSpin: () => Promise<unknown>
  onClose: () => void
}) {
  const canvas = useRef<HTMLCanvasElement>(null)
  const [rotation, setRotation] = useState(0)
  const [result, setResult] = useState<{ index: number; couponCode: string | null } | null>(null)
  const [spinning, setSpinning] = useState(false)
  const [problem, setProblem] = useState('')

  useEffect(() => {
    draw(canvas.current, campaign, rotation)
  }, [campaign, rotation])

  async function startSpin() {
    if (spinning || busy) return
    setProblem('')
    setResult(null)
    let index: number
    let couponCode: string | null = null
    try {
      const answer = (await onSpin()) as { segmentIndex?: unknown; couponCode?: unknown } | null
      if (typeof answer?.segmentIndex !== 'number') {
        // A 2xx this screen cannot animate. Nothing was refused, so repeating the click is honest.
        setProblem(spinBrokenNote())
        return
      }
      index = answer.segmentIndex
      couponCode = typeof answer.couponCode === 'string' ? answer.couponCode : null
    } catch (failure) {
      // §9.361 - the server's own sentence. It said *"Dnes ste už točili. Skúste znova zajtra."* and
      // this screen used to answer *"Skúste to znova"*, for the next twenty-four hours.
      setProblem(spinRefusal(failure))
      return
    }

    const destination = spinDestination(rotation, index, campaign.segments.length)
    if (destination === null) {
      // The server named a segment this wheel does not have. Show the prize and do not spin, rather
      // than spinning to a slice picked by arithmetic on a bad index.
      setResult({ index, couponCode })
      return
    }

    setSpinning(true)
    const from = rotation
    const started = performance.now()
    const step = (frameAt: number) => {
      const elapsed = frameAt - started
      // `rotationAt` takes elapsed **milliseconds** and eases inside; handing it a progress fraction
      // would ease twice and the wheel would crawl to a stop in the first frame.
      setRotation(rotationAt(from, destination, elapsed))
      if (elapsed < SPIN_DURATION_MS) {
        requestAnimationFrame(step)
        return
      }
      setRotation(destination)
      setSpinning(false)
      setResult({ index, couponCode })
    }
    requestAnimationFrame(step)
  }

  const landed = result === null ? undefined : landedOn(campaign, result.index)

  /* §9.489, `TODO.md` 3f - it said `aria-modal="true"` and confined nothing. The role is on the card because
     the wrapper is the dismiss scrim (`role="presentation"` plus `onClick={onClose}`), and the close is
     unconditional to match that tap. `data-wheel-close` is marked so the opening focus lands on *Zavrieť*
     rather than on *Zatočiť*, which spends the customer's one spin of the day. */
  const panelRef = useRef<HTMLDivElement>(null)
  const { onKeyDown } = useModalFocus(true, panelRef, onClose)

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      data-wheel-dialog
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={tt('Koleso šťastia')}
        ref={panelRef}
        onKeyDown={onKeyDown}
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-sm rounded-3xl bg-background border border-border shadow-2xl p-6 text-center space-y-4"
      >
        <h2 className="text-xl font-bold">
          <T>Koleso šťastia</T>
        </h2>

        <div className="relative mx-auto" style={{ width: 260, height: 260 }}>
          {/* The pointer sits at the top, which is the `-π/2` the geometry aligns segments to. */}
          <div
            aria-hidden="true"
            className="absolute left-1/2 -translate-x-1/2 -top-1 z-10 w-0 h-0 border-l-8 border-r-8 border-t-[14px] border-l-transparent border-r-transparent border-t-foreground"
          />
          <canvas ref={canvas} width={260} height={260} data-wheel-canvas className="rounded-full" />
        </div>

        {result !== null ? (
          <p className="text-sm font-semibold" data-wheel-result={result.index}>
            {prizeNoteOf(landed, result.couponCode)}
          </p>
        ) : campaign.nextSpinAt !== null && !campaign.canSpin ? (
          /* Legacy's own two lines: the label, then a live `hh:mm:ss` under it. */
          <div className="text-center space-y-1">
            <p className="text-sm text-muted-foreground">
              <T>Ďalší spin o</T>
            </p>
            <Countdown to={campaign.nextSpinAt} />
          </div>
        ) : (
          <p className="text-xs text-muted-foreground" data-wheel-note>
            {wheelNote(campaign)}
          </p>
        )}

        {problem !== '' && (
          <p className="text-xs text-red-500" data-wheel-problem>
            {problem}
          </p>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            data-wheel-spin
            disabled={!campaign.canSpin || spinning || busy || result !== null}
            onClick={() => void startSpin()}
            className="flex-1 min-h-[48px] rounded-2xl bg-green-600 hover:bg-green-700 text-white font-bold disabled:opacity-50 transition-colors"
          >
            {spinning ? <T>Točím…</T> : <T>Zatočiť</T>}
          </button>
          <button
            type="button"
            data-wheel-close
            data-dialog-cancel
            onClick={onClose}
            className="min-h-[48px] px-4 rounded-2xl border border-border text-sm font-semibold"
          >
            <T>Zavrieť</T>
          </button>
        </div>
      </div>
    </div>
  )
}

/** The wheel itself. Canvas rather than SVG because legacy draws it that way and it is one shape. */
function draw(element: HTMLCanvasElement | null, campaign: WheelCampaign, rotation: number): void {
  const context = element?.getContext('2d')
  if (element === null || context === null || context === undefined) return

  const size = element.width
  const radius = size / 2
  const arc = segmentArc(campaign.segments.length)
  context.clearRect(0, 0, size, size)

  campaign.segments.forEach((segment, index) => {
    const start = rotation + index * arc
    context.beginPath()
    context.moveTo(radius, radius)
    context.arc(radius, radius, radius - 4, start, start + arc)
    context.closePath()
    context.fillStyle = segment.color
    context.fill()
    context.strokeStyle = 'rgba(255,255,255,0.6)'
    context.lineWidth = 2
    context.stroke()

    context.save()
    context.translate(radius, radius)
    context.rotate(start + arc / 2)
    context.textAlign = 'right'
    context.fillStyle = '#ffffff'
    context.font = 'bold 12px sans-serif'
    // Clipped rather than shrunk: a franchise can write a sentence on a slice, and a wheel of
    // three-point text is less readable than a wheel of truncated labels.
    context.fillText(segment.label.slice(0, 16), radius - 14, 4)
    context.restore()
  })

  context.beginPath()
  context.arc(radius, radius, 18, 0, Math.PI * 2)
  context.fillStyle = '#ffffff'
  context.fill()
  context.strokeStyle = 'rgba(0,0,0,0.1)'
  context.stroke()
}
